import Vue from 'vue'
import {ref, del, computed, nextTick} from '@vue/composition-api'

// Notification
import {useToast} from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {getTable, getData, searchOptions, editColumn, exportData, keywordsAddToGroup} from "@/api/table";

export default function useTable(props, context) {
  // Use toast
  const toast = useToast()

  const refTable = ref(null);
  const table = ref(null);
  const data = ref([]);
  const pagination = ref(null)

  const loading = ref(false)
  const perPageOptions = [10, 25, 50, 100]

  const perPage = computed(() => {
    return pagination.value ? pagination.value.per_page : 35
  })

  const currentPage = computed(() => {
    return pagination.value ? pagination.value.current_page : 1
  })

  const lastPage = computed(() => {
    return pagination.value ? pagination.value.last_page : 1
  })

  const total = computed(() => {
    return pagination.value ? pagination.value.total : 0
  })

  const showingFrom = computed(() => {
    return pagination.value ? pagination.value.from : 0
  })

  const showingTo = computed(() => {
    return pagination.value ? pagination.value.to : 0
  })

  const sortKey = computed(() => {
    return table.value ? table.value.sort_key : 'id'
  })

  const sortDirection = computed(() => {
    return table.value ? table.value.sort_direction : 'desc'
  })

  const columns = computed(() => {
    return table.value ? table.value.columns : []
  })

  const defaultExportColumns = computed(() => {
    return table.value ? table.value.default_export_columns : []
  })

  const allExportColumns = computed(() => {
    return table.value ? table.value.all_export_columns : []
  })

  const filters = computed(() => {
    return table.value ? table.value.filters : []
  })

  const selectedFilters = computed(() => {
    return filters.value.filter(i => i.value)
  })

  const updatePagination = (props) => {
    if (props.page) {
      pagination.value.current_page = props.page
    }
    if (props.perPage) {
      pagination.value.current_page = 1
      pagination.value.per_page = props.perPage
    }
    fetchData()
  }

  const updateSort = ({sortBy, sortDesc}) => {

    if (sortBy) {
      table.value.sort_key = sortBy
      table.value.sort_direction = sortDesc ? 'desc' : 'asc'

      fetchData()
    }
  }

  const fetchTable = async () => {
    loading.value = true

    const storageData = JSON.parse(localStorage.getItem(props.storageKey));
    const additionParams = storageData && storageData.additionParams || props.additionParams

    try {
      const response = await getTable({
        _name: props.name,
        _namespace: props.namespace,
        _scope_name: props.scopeName,
        _scope_param: props.scopeParam,
        _addition_params: additionParams,
      });

      context.emit('update:addition-params', additionParams)

      if (response.filters.length && storageData && storageData.filters.length){
        response.filters.map(i => {
          const filter = storageData.filters.find(f => f.key === i.key);
          if (filter) {
            i.value = filter.value
          }
        })
      }

      nextTick(() => {

        table.value = response;
        loading.value = false

        fetchData()
      })

    } catch (e) {
      console.error(e)
      loading.value = false

      toast({
        component: ToastificationContent,
        props: {
          title: "Error fetching table",
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    }
  }

  const updateStorage = () => {

    if (props.storageKey) {
      localStorage.setItem(props.storageKey, JSON.stringify({
        additionParams: props.additionParams,
        page: currentPage.value,
        per_page: perPage.value,
        sort_key: sortKey.value,
        sort_direction: sortDirection.value,
        filters: prepareFiltersForRequest(filters)
      }))
    }
  }

  const fetchData = async () => {
    loading.value = true;

    try {
      const response = await getData({
        _name: props.name,
        _namespace: props.namespace,
        _scope_name: props.scopeName,
        _scope_param: props.scopeParam,
        _addition_params: props.additionParams,
        page: currentPage.value,
        per_page: perPage.value,
        sort_key: sortKey.value,
        sort_direction: sortDirection.value,
        filters: prepareFiltersForRequest(filters)
      });

      data.value = response.data
      pagination.value = response.pagination
      loading.value = false

      updateStorage()
    } catch (response) {
      console.error(response)

      if (response.status_code === 422) {
        setFiltersError(response);
      }

      loading.value = false

      toast({
        component: ToastificationContent,
        props: {
          title: "Error fetching table",
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    }
  }

  const fetchFilterOptions = async (filter, search) => {

    search.loading(true)

    try {
      const response = await searchOptions({
        _name: props.name,
        _namespace: props.namespace,
        filter_key: filter.key,
        search: search.query
      });

      Vue.set(filter, 'options', response)

      search.loading(false)

    } catch (e) {
      search.loading(false)
      console.log(e)
    }
  }

  const fetchEditColumn = (column, item) => {
    return editColumn({
      _name: props.name,
      _namespace: props.namespace,
      _column: column.key,
      ...item
    })
  }

  const fetchExportData = async (columns, loaded) => {
    clearFilterErrors()

    try {
      const response = await exportData({
        _name: props.name,
        _namespace: props.namespace,
        _scope_name: props.scopeName,
        _scope_param: props.scopeParam,
        sort_key: sortKey.value,
        sort_direction: sortDirection.value,
        filters: prepareFiltersForRequest(filters),
        columns: columns
      });

      console.log(response);

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'export.csv'); //or any other extension
      document.body.appendChild(link);
      link.click();

      loaded()

    } catch (error) {
      console.error(error)

      if (e.status_code === 422) {
        setFiltersError(response);
      }

      toast({
        component: ToastificationContent,
        props: {
          title: "Error fetching table",
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
      loaded()
    }
  }

  const fetchKeywordsAddToGroup = async (group, page_or_all, loaded) => {
    try {
      await keywordsAddToGroup({
        _name: props.name,
        _namespace: props.namespace,
        _scope_name: props.scopeName,
        _scope_param: props.scopeParam,
        per_page: perPage.value,
        sort_key: sortKey.value,
        sort_direction: sortDirection.value,
        filters: prepareFiltersForRequest(filters),
        page_or_all: page_or_all,
        group: group
      });

      loaded()

    } catch (error) {

      loaded()
    }
  }


  const setFiltersError = (response) => {
    Object.entries(response.errors).forEach(entry => {
      const [key, value] = entry;
      Vue.set(filters.value.find(filter => filter.key === key), 'errors', value)
    })
  }

  const clearFilterErrors = () => {
    console.log(filters.value)
    filters.value.filter(filter => filter.errors).forEach(filter => del(filter, 'errors'))
  }

  return {
    fetchTable,
    fetchData,
    fetchFilterOptions,
    fetchEditColumn,
    fetchExportData,
    fetchKeywordsAddToGroup,
    updateSort,
    updatePagination,
    updateStorage,

    refTable,
    table,
    columns,
    allExportColumns,
    defaultExportColumns,
    filters,
    selectedFilters,
    data,

    currentPage,
    lastPage,
    perPage,
    showingFrom,
    showingTo,
    sortKey,
    sortDirection,
    total,

    loading,
    perPageOptions,
  }
}

function prepareFiltersForRequest(filters) {
  return filters.value.filter(filter => filter.value).map(filter => {
    return {key: filter.key, value: filter.value}
  })
}
